import React, { useRef } from 'react';

/**
 * A marker box that can be dragged to move and resized from 8 handles.
 * Position values (x, y, w, h) are percentages of the container's inner element.
 *
 * Props:
 *  - marker: { x, y, w, h, barn, type }
 *  - containerRef: ref to the inner scaled element (used for percent math)
 *  - onChange(newMarker)
 *  - onClick() — called on a click without drag
 *  - disabled: true to disable drag/resize (e.g. when in drawing mode)
 *  - selected: highlight when true
 *  - variant: 'stall' | 'rv' | 'detail' — colour scheme
 *  - label: string shown above the box
 *  - testId
 */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export const DraggableMarker = ({
  marker,
  containerRef,
  onChange,
  onClick,
  disabled = false,
  selected = false,
  variant = 'stall',
  label,
  testId,
}) => {
  const stateRef = useRef(null);

  const beginDrag = (e, handle) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef?.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const point = e.touches?.[0] || e;
    stateRef.current = {
      handle,
      startX: point.clientX,
      startY: point.clientY,
      x: marker.x,
      y: marker.y,
      w: marker.w || 10,
      h: marker.h || 10,
      rectW: rect.width,
      rectH: rect.height,
      moved: false,
    };

    const onMove = (ev) => {
      const s = stateRef.current;
      if (!s) return;
      const p = ev.touches?.[0] || ev;
      const dx = ((p.clientX - s.startX) / s.rectW) * 100;
      const dy = ((p.clientY - s.startY) / s.rectH) * 100;
      if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) s.moved = true;

      let { x, y, w, h } = s;
      if (s.handle === 'move') {
        x = clamp(x + dx, 0, Math.max(0, 100 - w));
        y = clamp(y + dy, 0, Math.max(0, 100 - h));
      } else {
        if (s.handle.includes('e')) w = clamp(w + dx, 1, 100 - x);
        if (s.handle.includes('s')) h = clamp(h + dy, 1, 100 - y);
        if (s.handle.includes('w')) {
          const newX = clamp(x + dx, 0, x + w - 1);
          w = w + (x - newX);
          x = newX;
        }
        if (s.handle.includes('n')) {
          const newY = clamp(y + dy, 0, y + h - 1);
          h = h + (y - newY);
          y = newY;
        }
      }
      onChange?.({
        ...marker,
        x: +x.toFixed(2),
        y: +y.toFixed(2),
        w: +w.toFixed(2),
        h: +h.toFixed(2),
      });
      if (ev.cancelable) ev.preventDefault();
    };

    const onUp = () => {
      const s = stateRef.current;
      stateRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      if (s && !s.moved && s.handle === 'move') onClick?.();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };

  const colours = {
    stall: { border: '#FF9500', bg: 'rgba(255,149,0,0.12)', text: '#FF9500' },
    rv: { border: '#AF52DE', bg: 'rgba(175,82,222,0.12)', text: '#AF52DE' },
    detail: { border: '#0096FF', bg: 'rgba(0,150,255,0.12)', text: '#0096FF' },
  };
  const c = colours[variant] || colours.stall;
  const selectedColor = '#0096FF';

  const handles = [
    { id: 'nw', cls: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize' },
    { id: 'n', cls: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize' },
    { id: 'ne', cls: 'top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize' },
    { id: 'e', cls: 'top-1/2 right-0 translate-x-1/2 -translate-y-1/2 cursor-ew-resize' },
    { id: 'se', cls: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize' },
    { id: 's', cls: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-ns-resize' },
    { id: 'sw', cls: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize' },
    { id: 'w', cls: 'top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize' },
  ];

  return (
    <div
      className={`absolute border-2 rounded-sm ${disabled ? '' : 'cursor-move'} touch-none`}
      style={{
        left: `${marker.x}%`,
        top: `${marker.y}%`,
        width: `${marker.w || 10}%`,
        height: `${marker.h || 10}%`,
        borderColor: selected ? selectedColor : c.border,
        background: selected ? 'rgba(0,150,255,0.15)' : c.bg,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onPointerDown={(e) => beginDrag(e, 'move')}
      onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
      onTouchStart={(e) => { e.stopPropagation(); }}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
    >
      {label !== '' && (
        <span
          className="absolute -top-4 left-0 text-[8px] font-bold px-1 py-0.5 rounded bg-[#0A0A0A]/90 whitespace-nowrap pointer-events-none"
          style={{ color: selected ? selectedColor : c.text }}
        >
          {label || marker.barn || 'Region'}
        </span>
      )}
      {!disabled && selected && handles.map((h) => (
        <div
          key={h.id}
          className={`absolute w-2.5 h-2.5 bg-white border border-[#0096FF] rounded-sm ${h.cls}`}
          onPointerDown={(e) => beginDrag(e, h.id)}
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onTouchStart={(e) => { e.stopPropagation(); beginDrag(e, h.id); }}
          onClick={(e) => e.stopPropagation()}
          data-testid={testId ? `${testId}-handle-${h.id}` : undefined}
        />
      ))}
    </div>
  );
};

export default DraggableMarker;
